import { useState, useEffect } from 'react';
import { API } from '../config/api';

/**
 * Custom hook to fetch admin settings (statuses, milestones, task templates)
 * This ensures the Case Details page uses dynamic data from the database
 * instead of hardcoded values.
 */
export function useAdminSettings() {
  const [statuses, setStatuses] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all three settings in parallel
        const [statusesRes, milestonesRes, templatesRes] = await Promise.all([
          fetch(`${API}/admin/settings/statuses`, { credentials: 'include' }),
          fetch(`${API}/admin/settings/milestones`, { credentials: 'include' }),
          fetch(`${API}/admin/settings/task-templates`, { credentials: 'include' })
        ]);

        if (statusesRes.ok) {
          const data = await statusesRes.json();
          setStatuses(data.statuses || []);
        }

        if (milestonesRes.ok) {
          const data = await milestonesRes.json();
          setMilestones(data.milestones || []);
        }

        if (templatesRes.ok) {
          const data = await templatesRes.json();
          setTaskTemplates(data.templates || []);
        }
      } catch (err) {
        console.error('Failed to fetch admin settings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Helper function to get status options for dropdown
  const getStatusOptions = () => {
    return statuses.map(s => ({
      value: s.value,
      label: s.label
    }));
  };

  // Helper function to get status description
  const getStatusDescription = (value) => {
    const status = statuses.find(s => s.value === value);
    return status?.description || '';
  };

  // Helper function to get milestone options for dropdown
  const getMilestoneOptions = () => {
    return milestones.map(m => ({
      value: m.value,
      label: m.label
    }));
  };

  // Helper function to get milestone description
  const getMilestoneDescription = (value) => {
    const milestone = milestones.find(m => m.value === value);
    return milestone?.description || '';
  };

  // Helper function to get milestone label
  const getMilestoneLabel = (value) => {
    const milestone = milestones.find(m => m.value === value);
    return milestone?.label || value.replace(/_/g, ' ');
  };

  // Helper function to get task templates by case type
  const getTaskTemplatesByCaseType = (caseType) => {
    const normalizedType = caseType?.toLowerCase() || '';
    
    // Check if it's an employment case
    const isEmployment = normalizedType.includes('employment') || 
                        normalizedType.includes('termination') || 
                        normalizedType.includes('wrongful') ||
                        normalizedType.includes('harassment') ||
                        normalizedType.includes('discrimination');
    
    const targetCaseType = isEmployment ? 'employment' : 'personal_injury';
    
    return taskTemplates
      .filter(t => t.case_type === targetCaseType || t.case_type === 'all')
      .map(t => ({
        id: t.template_id,
        title: t.title,
        description: t.description,
        whyNeeded: t.why_needed,
        category: t.category
      }));
  };

  return {
    statuses,
    milestones,
    taskTemplates,
    loading,
    error,
    getStatusOptions,
    getStatusDescription,
    getMilestoneOptions,
    getMilestoneDescription,
    getMilestoneLabel,
    getTaskTemplatesByCaseType
  };
}

export default useAdminSettings;
